'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { portfolioItems as staticItems } from '@/data/siteData';

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  image: string;
  url: string;
}

function PortfolioCard({ item }: { item: PortfolioItem }) {
  return (
    <div className="portfolio-card bg-white rounded-2xl flex flex-col h-full" style={{ boxShadow: '0 4px 24px rgba(72,67,142,0.13)' }}>
      {/* Image area - centered with light bg */}
      <div className="flex items-center justify-center pt-7 pb-4 px-6">
        <div className="w-full flex items-center justify-center rounded-xl overflow-hidden bg-[#eef3f8]" style={{ height: '160px' }}>
          <img
            src={item.image}
            alt={item.title}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 px-6 pb-6 pt-1">
        <h3 className="text-[#48438e] text-base font-bold leading-snug mb-2 line-clamp-2 text-center">
          {item.title}
        </h3>
        {/* Decorative lines like reference */}
        <div className="flex flex-col gap-1.5 mb-4">
          <div className="h-2 rounded-full bg-[#d6d4ef] w-full" />
          <div className="h-2 rounded-full bg-[#d6d4ef] w-4/5 mx-auto" />
          <div className="h-2 rounded-full bg-[#d6d4ef] w-3/5 mx-auto" />
        </div>
        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 flex-1 mb-4 text-center">
          {item.description}
        </p>
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 bg-[#48438e] hover:bg-[#3a3578] text-white text-sm font-semibold rounded-xl py-2.5 px-5 transition-all duration-200 shadow-sm hover:shadow-md"
        >
          Lihat Project
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  );
}

function PortfolioCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl flex flex-col" style={{ height: '380px', boxShadow: '0 4px 24px rgba(72,67,142,0.10)' }}>
      <div className="flex items-center justify-center pt-7 pb-4 px-6">
        <div className="w-full rounded-xl bg-gray-200 animate-pulse" style={{ height: '160px' }} />
      </div>
      <div className="flex flex-col flex-1 px-6 pb-6 pt-1 gap-3">
        <div className="h-4 bg-gray-200 rounded-lg animate-pulse w-3/4 mx-auto" />
        <div className="flex flex-col gap-1.5">
          <div className="h-2 bg-gray-200 rounded-full animate-pulse w-full" />
          <div className="h-2 bg-gray-200 rounded-full animate-pulse w-4/5 mx-auto" />
          <div className="h-2 bg-gray-200 rounded-full animate-pulse w-3/5 mx-auto" />
        </div>
        <div className="h-10 bg-gray-200 rounded-xl animate-pulse mt-auto" />
      </div>
    </div>
  );
}

export default function PortfolioSection() {
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    fetch('/api/portfolio')
      .then((res) => res.json())
      .then((data) => {
        if (data?.items?.length > 0) {
          setPortfolioItems(data.items);
        } else {
          setPortfolioItems(staticItems);
        }
      })
      .catch(() => {
        setPortfolioItems(staticItems);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="portfolio" className="py-20 px-4 sm:px-6 lg:px-8 text-[#48438e]">
      <div className="max-w-6xl mx-auto space-y-12">
        {/* Header */}
        <div className="flex flex-col gap-2 items-center text-center">
          <span className="text-sm font-semibold tracking-widest uppercase text-[#0090AD]">Karya Saya</span>
          <h2 className="text-4xl font-bold">Portfolio</h2>
          <p className="text-base text-gray-500 max-w-md">Berikut beberapa project yang pernah saya kerjakan</p>
          <div className="w-16 h-1 rounded-full bg-[#48438e] mt-1" />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <PortfolioCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="portfolio-swiper-wrapper relative mx-6">
            {/* Custom prev/next arrows outside cards */}
            <button
              ref={prevRef}
              className="portfolio-custom-prev absolute -left-5 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-white text-[#48438e] border border-gray-200 shadow-md hover:bg-[#48438e] hover:text-white transition-all duration-200"
              aria-label="Previous"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              ref={nextRef}
              className="portfolio-custom-next absolute -right-5 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-white text-[#48438e] border border-gray-200 shadow-md hover:bg-[#48438e] hover:text-white transition-all duration-200"
              aria-label="Next"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <Swiper
              modules={[Navigation, Pagination, Autoplay, A11y]}
              spaceBetween={24}
              slidesPerView={1}
              navigation={{
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }}
              onBeforeInit={(swiper) => {
                if (typeof swiper.params.navigation !== 'boolean' && swiper.params.navigation) {
                  swiper.params.navigation.prevEl = prevRef.current;
                  swiper.params.navigation.nextEl = nextRef.current;
                }
              }}
              pagination={{ clickable: true }}
              autoplay={{
                delay: 3500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              speed={700}
              loop={portfolioItems.length > 3}
              breakpoints={{
                640: { slidesPerView: 2, spaceBetween: 20 },
                1024: { slidesPerView: 3, spaceBetween: 24 },
              }}
              className="pb-16"
            >
              {portfolioItems.map((item) => (
                <SwiperSlide key={item.id} className="h-auto">
                  <PortfolioCard item={item} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      </div>

      <style jsx global>{`
        /* Equal height slides */
        .portfolio-swiper-wrapper .swiper-wrapper {
          align-items: stretch;
        }
        .portfolio-swiper-wrapper .swiper-slide {
          height: auto !important;
          display: flex;
        }
        .portfolio-swiper-wrapper .swiper-slide > div {
          width: 100%;
        }

        /* Hide default swiper nav buttons */
        .portfolio-swiper-wrapper .swiper-button-next,
        .portfolio-swiper-wrapper .swiper-button-prev {
          display: none;
        }

        /* Pagination dots */
        .portfolio-swiper-wrapper .swiper-pagination {
          bottom: 4px;
        }
        .portfolio-swiper-wrapper .swiper-pagination-bullet {
          background: #c4c2e0;
          opacity: 1;
          width: 8px;
          height: 8px;
          transition: all 0.3s ease;
        }
        .portfolio-swiper-wrapper .swiper-pagination-bullet-active {
          background: #48438e;
          width: 24px;
          border-radius: 4px;
        }
      `}</style>
    </section>
  );
}
